// client/src/components/unidades/UnidadeForm.js
import React from 'react';
import { MapPin, Phone, Mail, Search, Globe, Facebook, Instagram, Navigation, CheckCircle2 } from 'lucide-react';

const UnidadeForm = ({
    formData,
    onChange,
    onCepChange,
    errors = {},
    isEditing = true,
    isCreating = false,
    cepEncontrado = false,
    onUnlockCep,
}) => {
    const lockedStyle = { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' };
    return (
        <div className="form-content">
            <div className="form-grid-container">
                {/* Nome da Unidade */}
                <div className="form-group md:col-span-2">
                    <label className="required">Nome da Unidade</label>
                    <input
                        type="text"
                        name="nome"
                        value={formData.nome || ''}
                        onChange={onChange}
                        className={`form-input ${errors.nome ? 'border-red-500' : ''} ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Ex: Unidade Centro"
                        disabled={!isEditing}
                        required
                    />
                    {errors.nome && <div className="text-red-500 text-sm mt-1">{errors.nome}</div>}
                </div>

                {/* Código da Unidade */}
                <div className="form-group">
                    <label>Código</label>
                    <input
                        type="text"
                        name="codigo"
                        value={formData.codigo || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Ex: UNI001"
                        disabled={!isEditing}
                    />
                </div>

                {/* ===== ENDEREÇO ===== */}
                <div className="md:col-span-2 mt-4">
                    <h3 className="text-md font-medium text-gray-700 mb-2">Endereço</h3>
                </div>

                {/* CEP */}
                <div className="form-group">
                    <label className="required">CEP</label>
                    <div className="relative">
                        <input
                            type="text"
                            name="endereco.cep"
                            value={formData.endereco?.cep || ''}
                            onChange={onChange}
                            className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="00000-000"
                            maxLength="9"
                            disabled={!isEditing}
                            required
                        />
                        {isEditing && (
                            <Search
                                size={16}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-primary"
                                onClick={onCepChange}
                            />
                        )}
                    </div>
                    {isEditing && (cepEncontrado ? (
                        <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                            Endereço preenchido automaticamente.
                            <button type="button" onClick={onUnlockCep}
                                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                Editar manualmente
                            </button>
                        </div>
                    ) : (
                        <div className="form-hint" style={{ marginTop: 4, fontSize: '0.8rem', color: '#64748b' }}>
                            Preencha o CEP e clique na lupa para buscar automaticamente.
                        </div>
                    ))}
                </div>

                {/* Logradouro */}
                <div className="form-group md:col-span-2">
                    <label className="required">Logradouro</label>
                    <input
                        type="text"
                        name="endereco.logradouro"
                        value={formData.endereco?.logradouro || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Rua, Avenida..."
                        disabled={!isEditing}
                        readOnly={cepEncontrado && isEditing}
                        style={cepEncontrado && isEditing ? lockedStyle : undefined}
                        required
                    />
                </div>

                {/* Número */}
                <div className="form-group">
                    <label className="required">Número</label>
                    <input
                        type="text"
                        name="endereco.numero"
                        value={formData.endereco?.numero || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="123"
                        disabled={!isEditing}
                        required
                    />
                </div>

                {/* Complemento */}
                <div className="form-group">
                    <label>Complemento</label>
                    <input
                        type="text"
                        name="endereco.complemento"
                        value={formData.endereco?.complemento || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Sala 101"
                        disabled={!isEditing}
                    />
                </div>

                {/* Bairro */}
                <div className="form-group">
                    <label className="required">Bairro</label>
                    <input
                        type="text"
                        name="endereco.bairro"
                        value={formData.endereco?.bairro || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Centro"
                        disabled={!isEditing}
                        readOnly={cepEncontrado && isEditing}
                        style={cepEncontrado && isEditing ? lockedStyle : undefined}
                        required
                    />
                </div>

                {/* Localidade (Cidade) */}
                <div className="form-group">
                    <label className="required">Cidade</label>
                    <input
                        type="text"
                        name="endereco.localidade"
                        value={formData.endereco?.localidade || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="São Paulo"
                        disabled={!isEditing}
                        readOnly={cepEncontrado && isEditing}
                        style={cepEncontrado && isEditing ? lockedStyle : undefined}
                        required
                    />
                </div>

                {/* UF */}
                <div className="form-group">
                    <label className="required">UF</label>
                    <input
                        type="text"
                        name="endereco.uf"
                        value={formData.endereco?.uf || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="SP"
                        maxLength="2"
                        disabled={!isEditing}
                        readOnly={cepEncontrado && isEditing}
                        style={{ textTransform: 'uppercase', ...(cepEncontrado && isEditing ? lockedStyle : {}) }}
                        required
                    />
                </div>

                {/* Ponto de Referência */}
                <div className="form-group md:col-span-2">
                    <label>Ponto de Referência</label>
                    <input
                        type="text"
                        name="endereco.pontoReferencia"
                        value={formData.endereco?.pontoReferencia || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Próximo ao shopping..."
                        disabled={!isEditing}
                    />
                </div>

                {/* ===== GEOLOCALIZAÇÃO ===== */}
                <div className="md:col-span-2 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <Navigation size={14} className="mr-2" />
                        Geolocalização (Mapa)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-group">
                            <label className="text-xs">Latitude</label>
                            <input
                                type="number"
                                step="any"
                                name="geolocalizacao.latitude"
                                value={formData.geolocalizacao?.latitude || ''}
                                onChange={onChange}
                                className={`form-input ${!isEditing ? 'bg-gray-100' : 'bg-white'}`}
                                placeholder="-23.550520"
                                disabled={!isEditing}
                            />
                        </div>
                        <div className="form-group">
                            <label className="text-xs">Longitude</label>
                            <input
                                type="number"
                                step="any"
                                name="geolocalizacao.longitude"
                                value={formData.geolocalizacao?.longitude || ''}
                                onChange={onChange}
                                className={`form-input ${!isEditing ? 'bg-gray-100' : 'bg-white'}`}
                                placeholder="-46.633308"
                                disabled={!isEditing}
                            />
                        </div>
                        <div className="form-group md:col-span-2">
                            <label className="text-xs">Endereço Formatado (Mapas)</label>
                            <input
                                type="text"
                                name="geolocalizacao.enderecoFormatado"
                                value={formData.geolocalizacao?.enderecoFormatado || ''}
                                onChange={onChange}
                                className={`form-input ${!isEditing ? 'bg-gray-100' : 'bg-white'}`}
                                placeholder="Av. Paulista, 1000 - Bela Vista..."
                                disabled={!isEditing}
                            />
                        </div>
                    </div>
                </div>

                {/* ===== CONTATO ===== */}
                <div className="md:col-span-2 mt-4">
                    <h3 className="text-md font-medium text-gray-700 mb-2">Contato</h3>
                </div>

                {/* Telefone */}
                <div className="form-group">
                    <label>Telefone</label>
                    <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="contato.telefone"
                            value={formData.contato?.telefone || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="(11) 3333-4444"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* Telefone 2 */}
                <div className="form-group">
                    <label>Telefone 2</label>
                    <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="contato.telefone2"
                            value={formData.contato?.telefone2 || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="(11) 5555-6666"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* WhatsApp */}
                <div className="form-group">
                    <label>WhatsApp</label>
                    <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="contato.whatsapp"
                            value={formData.contato?.whatsapp || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="(11) 99999-9999"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* Email */}
                <div className="form-group">
                    <label>Email</label>
                    <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="email"
                            name="contato.email"
                            value={formData.contato?.email || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="contato@unidade.com"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* Email 2 */}
                <div className="form-group">
                    <label>Email 2</label>
                    <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="email"
                            name="contato.email2"
                            value={formData.contato?.email2 || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="financeiro@unidade.com"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* Site */}
                <div className="form-group md:col-span-2">
                    <label>Site</label>
                    <div className="relative">
                        <Globe size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="url"
                            name="contato.site"
                            value={formData.contato?.site || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="https://www.unidade.com"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* Redes Sociais */}
                <div className="form-group">
                    <label>Facebook</label>
                    <div className="relative">
                        <Facebook size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="contato.redesSociais.facebook"
                            value={formData.contato?.redesSociais?.facebook || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="facebook.com/unidade"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Instagram</label>
                    <div className="relative">
                        <Instagram size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="contato.redesSociais.instagram"
                            value={formData.contato?.redesSociais?.instagram || ''}
                            onChange={onChange}
                            className={`form-input pl-10 ${!isEditing ? 'bg-gray-50' : ''}`}
                            placeholder="@unidade"
                            disabled={!isEditing}
                        />
                    </div>
                </div>

                {/* ===== METADADOS ===== */}
                <div className="md:col-span-2 mt-4">
                    <h3 className="text-md font-medium text-gray-700 mb-2">Informações Adicionais</h3>
                </div>

                {/* Capacidade */}
                <div className="form-group">
                    <label>Capacidade (pessoas)</label>
                    <input
                        type="number"
                        name="metadata.capacidade"
                        value={formData.metadata?.capacidade || 0}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        min="0"
                        disabled={!isEditing}
                    />
                </div>

                {/* Área Total */}
                <div className="form-group">
                    <label>Área Total (m²)</label>
                    <input
                        type="number"
                        name="metadata.areaTotal"
                        value={formData.metadata?.areaTotal || 0}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        min="0"
                        disabled={!isEditing}
                    />
                </div>

                {/* Classificação */}
                <div className="form-group">
                    <label>Classificação (0 a 5 Estrelas)</label>
                    <input
                        type="number"
                        name="metadata.classificacao"
                        value={formData.metadata?.classificacao || 0}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        min="0"
                        max="5"
                        step="0.1"
                        disabled={!isEditing}
                    />
                </div>

                {/* Tags */}
                <div className="form-group md:col-span-2">
                    <label>Tags (separadas por vírgula)</label>
                    <input
                        type="text"
                        name="metadata.tags"
                        value={formData.metadata?.tags || ''}
                        onChange={onChange}
                        className={`form-input ${!isEditing ? 'bg-gray-50' : ''}`}
                        placeholder="Estética, Laser, Premium..."
                        disabled={!isEditing}
                    />
                </div>

                <div className="form-group md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                    {/* Estacionamento */}
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="metadata.possuiEstacionamento"
                            checked={formData.metadata?.possuiEstacionamento || false}
                            onChange={(e) => {
                                const syntheticEvent = {
                                    target: {
                                        name: 'metadata.possuiEstacionamento',
                                        type: 'checkbox',
                                        checked: e.target.checked
                                    }
                                };
                                onChange(syntheticEvent);
                            }}
                            className="form-checkbox"
                            disabled={!isEditing}
                        />
                        <span className="checkbox-label-text">Possui Estacionamento</span>
                    </label>

                    {/* Acessibilidade */}
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="metadata.acessibilidade"
                            checked={formData.metadata?.acessibilidade || false}
                            onChange={(e) => {
                                const syntheticEvent = {
                                    target: {
                                        name: 'metadata.acessibilidade',
                                        type: 'checkbox',
                                        checked: e.target.checked
                                    }
                                };
                                onChange(syntheticEvent);
                            }}
                            className="form-checkbox"
                            disabled={!isEditing}
                        />
                        <span className="checkbox-label-text">Acessível para PCD</span>
                    </label>

                    {/* Wi-Fi */}
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="metadata.possuiWifi"
                            checked={formData.metadata?.possuiWifi !== false}
                            onChange={(e) => {
                                const syntheticEvent = {
                                    target: {
                                        name: 'metadata.possuiWifi',
                                        type: 'checkbox',
                                        checked: e.target.checked
                                    }
                                };
                                onChange(syntheticEvent);
                            }}
                            className="form-checkbox"
                            disabled={!isEditing}
                        />
                        <span className="checkbox-label-text">Wi-Fi Disponível</span>
                    </label>
                </div>

                {/* Status */}
                {!isCreating && (
                    <div className="form-group md:col-span-2 pt-4 border-t border-gray-100 mt-2">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="ativo"
                                checked={formData.ativo !== false}
                                onChange={(e) => {
                                    const syntheticEvent = {
                                        target: {
                                            name: 'ativo',
                                            type: 'checkbox',
                                            checked: e.target.checked
                                        }
                                    };
                                    onChange(syntheticEvent);
                                }}
                                className="form-checkbox"
                                disabled={!isEditing}
                            />
                            <span className="checkbox-label-text font-medium">Unidade Ativa</span>
                        </label>
                        <div className="form-hint ml-6">
                            Unidades inativas não aparecem em agendas e não podem ser selecionadas para novos vínculos.
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default UnidadeForm;